/**
 * Boli Live - Store Settings Page
 *
 * Store customization page for sellers to update their store info.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Store,
  Image as ImageIcon,
  FileText,
  Share2,
  Loader2,
  AlertCircle,
  CheckCircle,
  ExternalLink,
} from 'lucide-react';
import { getStoreService } from '@boli/core';
import type { Store as StoreType, UpdateStoreRequest, StorePolicies, StoreSocialLinks } from '@boli/types';
import { cn } from '../lib/utils';

interface FormData {
  name: string;
  description: string;
  logo: string;
  banner: string;
  returnPolicy: string;
  shippingPolicy: string;
  paymentMethods: string;
  instagram: string;
  facebook: string;
  whatsapp: string;
}

export default function StoreSettingsPage() {
  const [store, setStore] = useState<StoreType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load store data
  useEffect(() => {
    const fetchStore = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const storeService = getStoreService();
        const fetchedStore = await storeService.getMyStore();

        if (!fetchedStore) {
          setLoadError('Store not found. You may need to create a store first.');
          return;
        }

        setStore(fetchedStore);
        setFormData({
          name: fetchedStore.name || '',
          description: fetchedStore.description || '',
          logo: fetchedStore.logo || '',
          banner: fetchedStore.banner || '',
          returnPolicy: fetchedStore.policies?.returnPolicy || '',
          shippingPolicy: fetchedStore.policies?.shippingPolicy || '',
          paymentMethods: fetchedStore.policies?.paymentMethods?.join(', ') || '',
          instagram: fetchedStore.socialLinks?.instagram || '',
          facebook: fetchedStore.socialLinks?.facebook || '',
          whatsapp: fetchedStore.socialLinks?.whatsapp || '',
        });
      } catch {
        setLoadError('Failed to load store');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStore();
  }, []);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    if (!formData) return;
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
    // Clear error when field is updated
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    // Clear success message on edit
    if (successMessage) {
      setSuccessMessage(null);
    }
  };

  const validate = (): boolean => {
    if (!formData) return false;

    const errors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Store name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Store name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      errors.name = 'Store name must be less than 100 characters';
    }

    // Logo URL validation (optional)
    if (formData.logo.trim()) {
      try {
        new URL(formData.logo);
      } catch {
        errors.logo = 'Please enter a valid URL';
      }
    }

    // Banner URL validation (optional)
    if (formData.banner.trim()) {
      try {
        new URL(formData.banner);
      } catch {
        errors.banner = 'Please enter a valid URL';
      }
    }

    // Social links validation
    if (formData.instagram.trim() && !formData.instagram.startsWith('http')) {
      errors.instagram = 'Please enter a full URL (https://...)';
    }
    if (formData.facebook.trim() && !formData.facebook.startsWith('http')) {
      errors.facebook = 'Please enter a full URL (https://...)';
    }
    if (formData.whatsapp.trim() && !/^[\d+]+$/.test(formData.whatsapp.replace(/\s/g, ''))) {
      errors.whatsapp = 'Please enter a valid phone number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData) return;
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setSuccessMessage(null);

    try {
      const storeService = getStoreService();

      // Build policies object
      const policies: StorePolicies = {};
      if (formData.returnPolicy.trim()) {
        policies.returnPolicy = formData.returnPolicy.trim();
      }
      if (formData.shippingPolicy.trim()) {
        policies.shippingPolicy = formData.shippingPolicy.trim();
      }
      if (formData.paymentMethods.trim()) {
        policies.paymentMethods = formData.paymentMethods
          .split(',')
          .map((m) => m.trim())
          .filter(Boolean);
      }

      // Build social links object
      const socialLinks: StoreSocialLinks = {};
      if (formData.instagram.trim()) {
        socialLinks.instagram = formData.instagram.trim();
      }
      if (formData.facebook.trim()) {
        socialLinks.facebook = formData.facebook.trim();
      }
      if (formData.whatsapp.trim()) {
        socialLinks.whatsapp = formData.whatsapp.trim();
      }

      const updateRequest: UpdateStoreRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        logo: formData.logo.trim() || undefined,
        banner: formData.banner.trim() || undefined,
        policies: Object.keys(policies).length > 0 ? policies : undefined,
        socialLinks: Object.keys(socialLinks).length > 0 ? socialLinks : undefined,
      };

      const result = await storeService.updateStore(updateRequest);

      if (result.success) {
        setSuccessMessage('Store settings saved successfully');
        if (result.data) {
          setStore(result.data);
        }
      } else {
        setSubmitError(result.error?.message || 'Failed to save settings');
      }
    } catch {
      setSubmitError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error state (no store)
  if (loadError || !store || !formData) {
    return (
      <div className="min-h-screen bg-secondary/30">
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
          <div className="max-w-2xl mx-auto flex items-center gap-4 p-4">
            <Link
              to="/seller"
              className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-bold text-foreground">Store Settings</h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto p-4">
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <Store className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No Store Found</h2>
            <p className="text-muted-foreground mb-6">
              {loadError || 'You need to create a store before you can customize it.'}
            </p>
            <Link
              to="/seller"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-secondary/30 pb-8"
    >
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <Link
              to="/seller"
              className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-bold text-foreground">Store Settings</h1>
          </div>
          {store.slug && (
            <Link
              to={`/store/${store.slug}`}
              target="_blank"
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              View Store
              <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </motion.div>
        )}

        {/* Submit Error */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Error</p>
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <Store className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">Basic Information</h2>
          </div>

          <div className="space-y-4">
            {/* Store Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Store Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="My Amazing Store"
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.name ? 'border-red-500' : 'border-border'
                )}
              />
              {formErrors.name && (
                <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
              )}
            </div>

            {/* Store URL (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Store URL
              </label>
              <div className="flex items-center gap-2 px-4 py-2 bg-muted border border-border rounded-md text-muted-foreground">
                <span className="text-sm">boli.pk/store/</span>
                <span className="text-sm font-medium text-foreground">{store.slug}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Store URL cannot be changed after creation
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Tell customers about your store..."
                rows={3}
                className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <ImageIcon className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">Branding</h2>
          </div>

          <div className="space-y-4">
            {/* Logo URL */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Logo URL
              </label>
              <input
                type="text"
                value={formData.logo}
                onChange={(e) => updateField('logo', e.target.value)}
                placeholder="https://example.com/logo.png"
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.logo ? 'border-red-500' : 'border-border'
                )}
              />
              {formErrors.logo && (
                <p className="text-xs text-red-500 mt-1">{formErrors.logo}</p>
              )}
              {formData.logo && !formErrors.logo && (
                <div className="mt-2">
                  <img
                    src={formData.logo}
                    alt="Logo preview"
                    className="w-16 h-16 rounded-lg object-cover border border-border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Banner URL */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Banner URL
              </label>
              <input
                type="text"
                value={formData.banner}
                onChange={(e) => updateField('banner', e.target.value)}
                placeholder="https://example.com/banner.jpg"
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.banner ? 'border-red-500' : 'border-border'
                )}
              />
              {formErrors.banner && (
                <p className="text-xs text-red-500 mt-1">{formErrors.banner}</p>
              )}
              {formData.banner && !formErrors.banner && (
                <div className="mt-2">
                  <img
                    src={formData.banner}
                    alt="Banner preview"
                    className="w-full h-24 rounded-lg object-cover border border-border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Recommended size: 1200x300 pixels
              </p>
            </div>
          </div>
        </div>

        {/* Store Policies */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">Store Policies</h2>
          </div>

          <div className="space-y-4">
            {/* Return Policy */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Return Policy
              </label>
              <textarea
                value={formData.returnPolicy}
                onChange={(e) => updateField('returnPolicy', e.target.value)}
                placeholder="Describe your return and refund policy..."
                rows={3}
                className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>

            {/* Shipping Policy */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Shipping Policy
              </label>
              <textarea
                value={formData.shippingPolicy}
                onChange={(e) => updateField('shippingPolicy', e.target.value)}
                placeholder="Describe your shipping methods and delivery times..."
                rows={3}
                className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>

            {/* Payment Methods */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Payment Methods
              </label>
              <input
                type="text"
                value={formData.paymentMethods}
                onChange={(e) => updateField('paymentMethods', e.target.value)}
                placeholder="COD, JazzCash, Easypaisa, Bank Transfer"
                className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Separate multiple methods with commas
              </p>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <Share2 className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">Social Links</h2>
          </div>

          <div className="space-y-4">
            {/* Instagram */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Instagram
              </label>
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => updateField('instagram', e.target.value)}
                placeholder="https://instagram.com/yourstore"
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.instagram ? 'border-red-500' : 'border-border'
                )}
              />
              {formErrors.instagram && (
                <p className="text-xs text-red-500 mt-1">{formErrors.instagram}</p>
              )}
            </div>

            {/* Facebook */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Facebook
              </label>
              <input
                type="text"
                value={formData.facebook}
                onChange={(e) => updateField('facebook', e.target.value)}
                placeholder="https://facebook.com/yourstore"
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.facebook ? 'border-red-500' : 'border-border'
                )}
              />
              {formErrors.facebook && (
                <p className="text-xs text-red-500 mt-1">{formErrors.facebook}</p>
              )}
            </div>

            {/* WhatsApp */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                WhatsApp Number
              </label>
              <input
                type="text"
                value={formData.whatsapp}
                onChange={(e) => updateField('whatsapp', e.target.value)}
                placeholder="+92 300 1234567"
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.whatsapp ? 'border-red-500' : 'border-border'
                )}
              />
              {formErrors.whatsapp && (
                <p className="text-xs text-red-500 mt-1">{formErrors.whatsapp}</p>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-3">
          <Link
            to="/seller"
            className="flex-1 py-3 border border-border rounded-md font-semibold text-center hover:bg-muted transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className={cn(
              'flex-1 py-3 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
}
